import { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { AlertTriangle, Copy, Check, Download } from "lucide-react";

/**
 * Dialog that surfaces a detailed explanation of why the File System Access
 * folder picker (or a subsequent write) failed. Includes copyable
 * diagnostics so the user can share them with support.
 *
 * Controlled via `error`: pass an Error with .userMessage/.diagnostics or
 * null to hide the dialog.
 */
export default function FolderWriteErrorDialog({ error, onClose, onDownloadZip }) {
  const [copied, setCopied] = useState(false);
  if (!error) return null;

  const details = error.diagnostics || {};
  const diagText = [
    `Nome errore: ${error.name || "Error"}`,
    `Messaggio: ${error.message || ""}`,
    `URL: ${details.host || "n/d"}`,
    `Protocol: ${details.protocol || "n/d"}`,
    `Contesto sicuro (HTTPS): ${String(details.isSecureContext)}`,
    `Dentro iframe: ${String(details.inIframe)}`,
    `API disponibile: ${String(details.hasApi)}`,
    `Browser: ${details.userAgent || "n/d"}`,
    details.original ? `Errore originale: ${details.original}` : null,
    details.filename ? `File in errore: ${details.filename}` : null,
    details.folderName ? `Cartella target: ${details.folderName}` : null,
    details.writtenCount != null ? `File scritti prima dell'errore: ${details.writtenCount}` : null,
  ].filter(Boolean).join("\n");

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(diagText);
      setCopied(true);
      toast.success("Dettagli copiati negli appunti");
      setTimeout(() => setCopied(false), 2000);
    } catch (_) {
      toast.error("Copia non riuscita — seleziona manualmente il testo qui sotto");
    }
  };

  return (
    <Dialog open={!!error} onOpenChange={(open) => !open && onClose?.()}>
      <DialogContent
        className="max-w-2xl bg-[#0A1738] border border-white/[0.08] text-white"
        data-testid="folder-error-dialog"
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-geist text-white">
            <AlertTriangle className="h-5 w-5 text-red-400" />
            Selezione cartella fallita
          </DialogTitle>
          <DialogDescription className="font-geist-mono text-[11px] text-white/60 pt-2 leading-relaxed">
            {error.userMessage || error.message}
          </DialogDescription>
        </DialogHeader>

        <div className="mt-2">
          <div className="font-geist-mono text-[10px] uppercase tracking-widest text-white/40 mb-2">
            Diagnostica tecnica
          </div>
          <pre
            data-testid="folder-error-diagnostics"
            className="bg-black/40 border border-white/[0.08] rounded-sm p-3 text-[11px] font-geist-mono text-white/80 whitespace-pre-wrap select-all max-h-64 overflow-auto leading-relaxed"
          >
{diagText}
          </pre>
          <p className="mt-3 font-geist-mono text-[10px] text-white/50 leading-relaxed">
            Suggerimento: confronta questi valori con il PC su cui il salvataggio funziona.
            Se "API disponibile" è false o "Contesto sicuro" è false, la funzione non può funzionare.
            Se entrambi sono true ma vedi "NotAllowedError" / "SecurityError", il problema è quasi
            sempre una policy di Edge/Chrome imposta dall'IT — apri <code className="text-[#4D8AFF]">edge://policy</code>
            e cerca <code className="text-[#4D8AFF]">FileSystemAccess</code>.
          </p>
        </div>

        <div className="mt-4 flex flex-wrap gap-2 justify-end">
          {onDownloadZip && (
            <Button
              variant="ghost"
              onClick={() => { onDownloadZip(); onClose?.(); }}
              data-testid="folder-error-download-zip"
              className="rounded-sm font-geist-mono text-[11px] uppercase tracking-wider text-white/80 hover:bg-white/[0.06] hover:text-white"
            >
              <Download className="h-3.5 w-3.5 mr-1" /> Scarica ZIP
            </Button>
          )}
          <Button
            variant="ghost"
            onClick={copy}
            data-testid="folder-error-copy-btn"
            className="rounded-sm font-geist-mono text-[11px] uppercase tracking-wider text-[#4D8AFF] hover:bg-[#4D8AFF]/10 hover:text-white"
          >
            {copied ? <Check className="h-3.5 w-3.5 mr-1" /> : <Copy className="h-3.5 w-3.5 mr-1" />}
            {copied ? "Copiato" : "Copia diagnostica"}
          </Button>
          <Button
            onClick={() => onClose?.()}
            data-testid="folder-error-close-btn"
            className="rounded-sm bg-[#4D8AFF] hover:bg-[#75A6FF] text-[#050B1F] font-geist font-semibold h-9 px-4"
          >
            Chiudi
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
